import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../../shared/components/ui/LoadingSpinner'
import { useAccountContext } from '../../contexts'

interface ThresholdData {
  settingId: number
  deviceId: number
  sensorNumber: number
  warnHigh?: number
  warnLow?: number
  warnChange?: number
  warnFlags?: number
  changed?: boolean
}

export function SensorThresholds() {
  const { devices } = useAccountContext()
  const [selectedDeviceId, setSelectedDeviceId] = useState<number>(0)
  const [thresholds, setThresholds] = useState<ThresholdData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const devicesArray = Object.values(devices)

  // Load thresholds when device changes
  useEffect(() => {
    if (selectedDeviceId === 0) {
      setThresholds([])
      return
    }

    const fetchThresholds = async () => {
      setIsLoading(true)

      try {
        // TODO: Replace with actual API call from webapp services when available
        // Example: const response = await api.getSensorThresholds(selectedDeviceId)
        // For now, initialize with empty array
        setThresholds([])
      } catch (error) {
        console.error('Failed to load sensor thresholds:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchThresholds()
  }, [selectedDeviceId])

  const handleThresholdChange = (
    settingId: number,
    field: 'warnHigh' | 'warnLow' | 'warnChange' | 'warnFlags',
    value: string
  ) => {
    const numValue = value === '' ? undefined : Number(value)

    setThresholds((prev) =>
      prev.map((threshold) =>
        threshold.settingId === settingId ? { ...threshold, [field]: numValue, changed: true } : threshold
      )
    )
  }

  const handleSaveThreshold = async (threshold: ThresholdData) => {
    if (!threshold.changed) return

    setIsSaving(true)
    try {
      // TODO: Replace with actual API call from webapp services when available
      // Example: await api.updateSensorThreshold(threshold)
      console.log('Saving sensor threshold:', threshold)

      setThresholds((prev) => prev.map((t) => (t.settingId === threshold.settingId ? { ...t, changed: false } : t)))
    } catch (error) {
      console.error('Failed to save threshold:', error)
    } finally {
      setIsSaving(false)
    }
  }

  if (selectedDeviceId === 0) {
    return (
      <div className="p-6 text-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Device</h3>
        <p className="text-gray-600">Choose a device from the dropdown above to configure sensor alert thresholds.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Device Selection */}
      <div className="flex items-center space-x-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Device</label>
          <select
            value={selectedDeviceId}
            onChange={(e) => setSelectedDeviceId(Number(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
          >
            <option value={0}>Select a device...</option>
            {devicesArray.map((device: any) => (
              <option key={device.id} value={device.id}>
                {device.name || `Device ${device.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Thresholds Table */}
      {thresholds.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sensor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  High Warning
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Low Warning
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Change Warning
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Warning Flags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {thresholds.map((threshold) => (
                <tr key={threshold.settingId} className={threshold.changed ? 'bg-yellow-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">Sensor {threshold.sensorNumber}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      step="0.01"
                      value={threshold.warnHigh ?? ''}
                      onChange={(e) => handleThresholdChange(threshold.settingId, 'warnHigh', e.target.value)}
                      placeholder="High threshold"
                      className="text-sm border-gray-300 rounded-md w-24 px-2 py-1 border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      step="0.01"
                      value={threshold.warnLow ?? ''}
                      onChange={(e) => handleThresholdChange(threshold.settingId, 'warnLow', e.target.value)}
                      placeholder="Low threshold"
                      className="text-sm border-gray-300 rounded-md w-24 px-2 py-1 border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      step="0.01"
                      value={threshold.warnChange ?? ''}
                      onChange={(e) => handleThresholdChange(threshold.settingId, 'warnChange', e.target.value)}
                      placeholder="Change threshold"
                      className="text-sm border-gray-300 rounded-md w-24 px-2 py-1 border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      value={threshold.warnFlags ?? ''}
                      onChange={(e) => handleThresholdChange(threshold.settingId, 'warnFlags', e.target.value)}
                      placeholder="Flags"
                      className="text-sm border-gray-300 rounded-md w-20 px-2 py-1 border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {threshold.changed && (
                      <button
                        onClick={() => handleSaveThreshold(threshold)}
                        disabled={isSaving}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Sensor Thresholds</h3>
          <p className="text-gray-600">No sensor threshold configurations found for the selected device.</p>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="text-sm text-blue-700">
          <h4 className="font-medium mb-2">Threshold Configuration Help:</h4>
          <ul className="space-y-1 text-xs">
            <li>
              <strong>High Warning:</strong> Alert when sensor value exceeds this threshold
            </li>
            <li>
              <strong>Low Warning:</strong> Alert when sensor value falls below this threshold
            </li>
            <li>
              <strong>Change Warning:</strong> Alert when sensor value changes by this amount
            </li>
            <li>
              <strong>Warning Flags:</strong> Numeric flags for custom alert conditions
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
